import React, { useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Textarea from '../shared/TailwindComponents/Textarea';
import FieldShell from '../shared/TailwindComponents/FieldShell';
import RuleFormSection from './RuleFormSection';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import {
  addGlobalRule,
  updateGlobalRule,
} from '../../store/actions/Review/ReviewActions';
import { GlobalRule, FindingSeverity } from '../../types/review';

interface GlobalRuleFormPanelProps {
  reviewUuid: string;
  rule?: GlobalRule;
  onClose: () => void;
}

const severityConfig: { key: FindingSeverity; label: string; color: string; activeColor: string }[] = [
  { key: 'critical', label: 'Critical', color: 'border-danger-300 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20', activeColor: 'border-danger-500 bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 ring-1 ring-danger-500' },
  { key: 'major', label: 'Major', color: 'border-warning-300 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20', activeColor: 'border-warning-500 bg-warning-50 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300 ring-1 ring-warning-500' },
  { key: 'minor', label: 'Minor', color: 'border-warning-300 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20', activeColor: 'border-warning-500 bg-warning-50 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300 ring-1 ring-warning-500' },
  { key: 'info', label: 'Info', color: 'border-info-300 text-info-600 hover:bg-info-50 dark:hover:bg-info-900/20', activeColor: 'border-info-500 bg-info-50 dark:bg-info-900/30 text-info-700 dark:text-info-300 ring-1 ring-info-500' },
];

const GlobalRuleFormPanel = forwardRef<any, GlobalRuleFormPanelProps>(({ reviewUuid, rule, onClose }, ref) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isEditing = !!rule;

  const [ruleText, setRuleText] = useState(rule?.rule_text || '');
  const [severity, setSeverity] = useState<FindingSeverity>(rule?.severity || 'major');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!ruleText.trim()) return;
    setIsSaving(true);
    try {
      if (isEditing && rule) {
        await dispatch(updateGlobalRule(reviewUuid, rule.uuid, { rule_text: ruleText, severity }) as any);
      } else {
        await dispatch(addGlobalRule(reviewUuid, { rule_text: ruleText, severity }) as any);
      }
      onClose();
    } catch (err: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: err?.message || t('Failed to save global rule'),
        duration: 3000,
      }) as any);
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, reviewUuid, ruleText, severity, isEditing, rule, onClose, t]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  return (
    <div className="p-6 space-y-6">
      <RuleFormSection title={t('Rule definition')}>
        <Textarea
          id="panel-rule-text"
          label={t('Rule text')}
          placeholder={t('e.g. No liability caps below $1,000,000')}
          value={ruleText}
          onChange={(e) => setRuleText(e.target.value)}
          rows={4}
          required
        />

        <FieldShell
          label={t('Severity')}
          translate={false}
          helpText={t('How serious a breach of this rule is when the AI finds one.')}
        >
          <div className="grid grid-cols-4 gap-2">
            {severityConfig.map((sev) => (
              <button
                key={sev.key}
                type="button"
                aria-pressed={severity === sev.key}
                onClick={() => setSeverity(sev.key)}
                className={clsx(
                  'rounded-md border px-3 py-2 text-center text-xs font-medium',
                  'transition-colors duration-200 ease-slide',
                  // Not FOCUS_RING: that also paints the border champagne, which
                  // would erase the severity colour the button exists to show.
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60',
                  severity === sev.key ? sev.activeColor : sev.color,
                )}
              >
                {t(sev.label)}
              </button>
            ))}
          </div>
        </FieldShell>
      </RuleFormSection>
    </div>
  );
});

export default GlobalRuleFormPanel;
